import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { B2Accordion } from "@hdb/biome-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faChevronDown,
  faChevronRight,
  faEye,
  faMap,
} from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getChildBlocksByGateway,
  getDisplayLevelsByBlock,
} from "src/core/api/services/command-module/zone.service";
import type { Zone, ZoneAsset } from "../../components/types";
import SkeletonComponent from "src/shared/custom-spinner/skeleton.component";

type LocationTreeProps = {
  gatewayCode: string;
  zone?: Zone | null;
  assignedAssets?: ZoneAsset[];
  selectedBlockCode?: string;
  selectedFloorCode?: string;
  onSelectBlock: (blockCode: string, floorCode?: string) => void;
};

export function LocationTree({
  gatewayCode,
  zone,
  assignedAssets = [],
  selectedBlockCode,
  selectedFloorCode,
  onSelectBlock,
}: LocationTreeProps) {
  const location = useLocation();
  const [expandedBlockCode, setExpandedBlockCode] = useState<
    string | undefined
  >();

  const { data: blocksData, isLoading } = useQuery({
    queryKey: ["childBlocksByGateway", gatewayCode],
    queryFn: ({ signal }) => getChildBlocksByGateway(gatewayCode, signal),
    enabled: !!gatewayCode,
  });

  const blocks = (blocksData?.data || []).map((block: any) => ({
    blockCode: block.BlockCode || block.blockCode,
    blockName: block.BlockName || block.blockName,
  }));

  useEffect(() => {
    setExpandedBlockCode(blocks[0]?.blockCode);
  }, [blocksData]);

  return (
    <SkeletonComponent
      wrapperHeight="224px"
      skeletonHeight="56px"
      count={4}
      isLoading={isLoading}
    >
      <div className="location-tree">
        {isLoading && (
          <div
            style={{
              padding: "1rem",
              textAlign: "center",
              color: "#64748b",
              fontSize: "0.85rem",
            }}
          >
            Loading locations...
          </div>
        )}
        {!isLoading && blocks.length === 0 && (
          <div
            style={{
              padding: "1rem",
              textAlign: "center",
              color: "#64748b",
              fontSize: "0.85rem",
            }}
          >
            No locations found.
          </div>
        )}
        {!isLoading && blocks.length > 0 && (
          <B2Accordion
            key={`${gatewayCode}-${blocks[0].blockCode}`}
            alwaysOpen={false}
            defaultActiveKey={blocks[0].blockCode}
            onSelect={(eventKey: string | null) =>
              setExpandedBlockCode(eventKey || undefined)
            }
          >
            {blocks.map((block: { blockCode: string; blockName?: string }) => (
              <B2Accordion.Item
                eventKey={block.blockCode}
                key={block.blockCode}
              >
                <B2Accordion.Header>
                  <span className="location-tree__header-content">
                    <FontAwesomeIcon
                      icon={
                        expandedBlockCode === block.blockCode
                          ? faChevronDown
                          : faChevronRight
                      }
                      className="location-tree__header-chevron"
                    />
                    <FontAwesomeIcon
                      icon={faBuilding}
                      className="location-tree__header-building"
                    />
                    <span className="location-tree__header-block">
                      {block.blockName || block.blockCode}
                    </span>
                  </span>
                </B2Accordion.Header>
                <B2Accordion.Body>
                  <LevelsList
                    blockCode={block.blockCode}
                    gatewayCode={gatewayCode}
                    zone={zone}
                    assignedAssets={assignedAssets}
                    returnPath={location.pathname}
                    selectedBlockCode={selectedBlockCode}
                    selectedFloorCode={selectedFloorCode}
                    onSelectLevel={onSelectBlock}
                  />
                </B2Accordion.Body>
              </B2Accordion.Item>
            ))}
          </B2Accordion>
        )}
      </div>
    </SkeletonComponent>
  );
}

type LevelsListProps = {
  blockCode: string;
  gatewayCode: string;
  zone?: Zone | null;
  assignedAssets: ZoneAsset[];
  returnPath: string;
  selectedBlockCode?: string;
  selectedFloorCode?: string;
  onSelectLevel: (blockCode: string, level: string) => void;
};

function LevelsList({
  blockCode,
  gatewayCode,
  zone,
  assignedAssets,
  returnPath,
  selectedBlockCode,
  selectedFloorCode,
  onSelectLevel,
}: LevelsListProps) {
  const navigate = useNavigate();
  const { data: floorsData, isLoading } = useQuery({
    queryKey: ["displayLevelsByBlock", gatewayCode, blockCode],
    queryFn: ({ signal }) => getDisplayLevelsByBlock(gatewayCode, blockCode, signal),
    enabled: !!blockCode,
  });

  const levels = (floorsData?.data || [])
    .map((level: any) => level.Level)
    .filter(Boolean);

  if (isLoading) {
    return (
      <SkeletonComponent
        wrapperHeight="120px"
        skeletonHeight="32px"
        count={3}
        isLoading={isLoading}
      >
        {null}
      </SkeletonComponent>
    );
  }

  if (levels.length === 0) {
    return (
      <div
        style={{
          padding: "0.5rem 0.75rem",
          color: "#94a3b8",
          fontSize: "0.8rem",
        }}
      >
        No levels found.
      </div>
    );
  }

  return (
    <>
      {levels.map((level: string) => {
        const isActive =
          selectedBlockCode === blockCode && selectedFloorCode === level;
        return (
          <div
            key={level}
            className={`location-tree__child ${isActive ? "location-tree__child--active" : ""}`}
            onClick={() => onSelectLevel(blockCode, level)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                onSelectLevel(blockCode, level);
              }
            }}
          >
            <span className="location-tree__child-left">
              <FontAwesomeIcon
                icon={faMap}
                className="location-tree__child-map"
              />
              <span>{level}</span>
            </span>
            <button
              type="button"
              className="location-tree__child-eye"
              title={`View ${level} floor plan`}
              aria-label={`View ${level} floor plan`}
              onClick={(event) => {
                event.stopPropagation();
                navigate(
                  `/asset/${btoa(gatewayCode)}/configuration/zones/floorplan/${btoa(blockCode)}/${encodeURIComponent(level)}?gatewayCode=${encodeURIComponent(gatewayCode)}`,
                  {
                    state: {
                      gatewayCode,
                      zone,
                      assignedAssets,
                      returnPath,
                    },
                  },
                );
              }}
            >
              <FontAwesomeIcon icon={faEye} />
            </button>
          </div>
        );
      })}
    </>
  );
}
